import { useReducer, useState } from 'react';
import { useDialog } from '../dialog/useDialog';
import { searchByOperationIdReducer, initialState, STEP } from './searchByOperationIdReducer';
import { fetchWithTokenRefresh } from '../../../api/fetchWithTokenRefresh';
import { handleResponseError } from '../../../api/handleResponseError';
import { useHandleApiError } from '../../../api/useHandleApiError';
import { operationDetailsBusinessError } from '../../core/portal/pages/airtime-and-data/billing/businessErrors';

interface Props {
  searchEndpoint: (operationId: string) => string;
}

export const useSearchByOperationId = ({ searchEndpoint }: Props) => {
  const [isSearching, setIsSearching] = useState(false);
  const { isDialogOpen, handleOpenDialog, handleCloseDialog } = useDialog();
  const [searchByOperationIdState, dispatch] = useReducer(searchByOperationIdReducer, initialState);
  const handleApiError = useHandleApiError();
  const handleSearchByOperationId = async (operationId: string) => {
    try {
      setIsSearching(true);
      const response = await fetchWithTokenRefresh(searchEndpoint(operationId));

      if (response.status !== 200) {
        await handleResponseError(response);
      }
      const data = await response.json();
      dispatch({ type: STEP.RESULTS, payload: data });
    } catch (code) {
      handleApiError({
        message: code,
        errorMapper: operationDetailsBusinessError,
        handleBusinessError: (error: string) => {
          dispatch({ type: STEP.SEARCH, payload: { error } });
        }
      });
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearchOperationOpenDialog = () => {
    dispatch({ type: STEP.SEARCH });
    handleOpenDialog();
  };

  return {
    openSearchOperationDialog: isDialogOpen,
    handleSearchOperationOpenDialog,
    handleSearchOperationCloseDialog: handleCloseDialog,
    searchByOperationIdState,
    handleSearchByOperationId,
    isSearching
  };
};
